import { Link, useNavigate } from "@tanstack/react-router";
import SignupConsent from "@auth/components/signup-consent";
import {
  FormField,
  FormPrefixedInput,
  FormInput,
  Button,
  Form,
  FormOTPInput,
} from "web-design-system";
import {
  useCheckArnAndEmail,
  useGenerateOtp,
  useSignupAsDistributor,
} from "creso-core/hooks";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import ThrottledButton from "@core/components/throttledButton";
import { handleError } from "@/utils/error";
import omitBy from "lodash/omitBy";
import isUndefined from "lodash/isUndefined";
import { arnSchema } from "creso-core/zod";
import { Route } from "@/routes/(authless)/signup/distributor";
import { reportSignupConversion } from "@/utils/gtag";
import { trackMetaSignup } from "@/utils/meta-pixel";

export const idSchema = z.object({
  arn_code: arnSchema,
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  phone_number: z
    .string()
    .regex(/^\+91\d{10}$/, "Phone number must be 10 digits")
    .min(1, "Phone number is required"),
  email_otp: z.string().min(1, "Email OTP is required"),
  phone_otp: z.string().min(1, "Phone OTP is required"),
});

type FormValues = z.infer<typeof idSchema>;

export default function IdForm() {
  const generateOtp = useGenerateOtp();
  const checkArnAndEmail = useCheckArnAndEmail();

  const { refer } = Route.useSearch();

  const form = useForm<FormValues>({
    resolver: zodResolver(idSchema),
    defaultValues: {
      arn_code: "",
      email: "",
      phone_number: "",
      email_otp: "",
      phone_otp: "",
    },
  });

  const [optGenerated, setOptGenerated] = useState(false);

  const handleOtpVerification = async (type?: "email" | "phone_number") => {
    try {
      const formValid = await form.trigger([
        "arn_code",
        "email",
        "phone_number",
      ]);
      if (!formValid) return;
      const result = await checkArnAndEmail.mutateAsync({
        arn: form.getValues("arn_code"),
        email: form.getValues("email"),
      });
      if (!result.is_valid) {
        form.setError("email", {
          message: "Email entered does not match with AMFI records",
        });
        setOptGenerated(false);
        return;
      }
      try {
        await generateOtp.mutateAsync(
          omitBy(
            {
              phone_number:
                !type || type === "phone_number"
                  ? form.getValues("phone_number")
                  : undefined,
              email:
                !type || type === "email" ? form.getValues("email") : undefined,
            },
            (v) => isUndefined(v) || v === ""
          )
        );
        setOptGenerated(true);
      } catch (error) {
        handleError(error, "Failed to generate OTP");
      }
    } catch (error) {
      handleError(error, "Failed to verify ARN and Email");
    }
  };

  const signupAsDistributor = useSignupAsDistributor();

  const navigate = useNavigate();

  const onSubmit = async (formData: FormValues) => {
    if (signupAsDistributor.isPending) return;

    try {
      const utmDetails = Object.fromEntries(
        new URLSearchParams(window.location.search).entries()
      );

      await signupAsDistributor.mutateAsync({
        arn_code: formData.arn_code,
        email: formData.email,
        phone_number: formData.phone_number,
        email_otp: formData.email_otp,
        phone_otp: formData.phone_otp,
        referred_by_handle: refer,
        ...(Object.keys(utmDetails).length > 0
          ? { utm_details: utmDetails }
          : {}),
      });
      trackMetaSignup();
      await reportSignupConversion(() => {
        navigate({
          to: "/welcome",
          replace: true,
          state: {
            allowedAccess: true,
          },
        });
      }, formData.email);
    } catch (error) {
      handleError(error, "Failed to signup");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <span className="text-lg font-semibold">ARN details</span>
      <Form {...form}>
        <form
          className="flex flex-col justify-between flex-1 gap-5"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-6 pb-8">
            <FormField
              name="arn_code"
              control={form.control}
              render={({ field }) => (
                <FormPrefixedInput
                  label="AMFI Registration Number"
                  {...field}
                  disabled={optGenerated}
                  prefix=""
                  type="number"
                  className="gap-2"
                >
                  ARN
                </FormPrefixedInput>
              )}
            />
            {!optGenerated && (
              <>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormInput
                      label="Email"
                      placeholder="Enter AMFI registered email"
                      {...field}
                      className="gap-2"
                    />
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone_number"
                  render={({ field }) => (
                    <FormPrefixedInput
                      {...field}
                      prefix="+91"
                      label="Phone number"
                      type="tel"
                      placeholder="Enter mobile number"
                      className="gap-2"
                    >
                      +91
                    </FormPrefixedInput>
                  )}
                />
              </>
            )}

            {optGenerated && (
              <>
                <FormField
                  control={form.control}
                  name="email_otp"
                  render={({ field }) => (
                    <div className="flex flex-col gap-1 relative">
                      <FormOTPInput
                        maxLength={6}
                        description={`Sent to: ${form.getValues("email")}`}
                        label="Email Otp"
                        {...field}
                        className="gap-2"
                      />
                      <ThrottledButton
                        throttle={5}
                        variant="link"
                        size="sm"
                        className="text-xs h-auto px-0 text-right absolute top-0 right-0"
                        onClick={() => handleOtpVerification("email")}
                      >
                        Resend OTP
                      </ThrottledButton>
                    </div>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone_otp"
                  render={({ field }) => (
                    <div className="flex flex-col gap-1 relative">
                      <FormOTPInput
                        maxLength={6}
                        description={`Sent to: ${form.getValues("phone_number")}`}
                        label="Phone Otp"
                        {...field}
                        className="gap-2"
                      />
                      <ThrottledButton
                        throttle={5}
                        variant="link"
                        size="sm"
                        className="text-xs h-auto px-0 text-right absolute top-0 right-0"
                        onClick={() => handleOtpVerification("phone_number")}
                      >
                        Resend OTP
                      </ThrottledButton>
                    </div>
                  )}
                />
              </>
            )}
          </div>

          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-5">
              <SignupConsent className="font-light" />
              {!optGenerated && (
                <Button
                  onClick={() => handleOtpVerification()}
                  type="button"
                  size="lg"
                >
                  Continue
                </Button>
              )}
              {optGenerated && (
                <Button
                  size="lg"
                  type="submit"
                  disabled={signupAsDistributor.isPending}
                >
                  Submit
                </Button>
              )}
            </div>
            <span className="text-sm font-medium text-center">
              Already a Partner?{" "}
              <Link className="underline" to="/login/distributor">
                Login
              </Link>
            </span>
          </div>
        </form>
      </Form>
    </div>
  );
}
